/**
 * OrderRequestsTab — Phlebotomist + OrgAdmin Lab linkage surface.
 * Flutter parity: `order_requests_tab.dart` (1423 lines).
 *
 * Columns (Flutter header order):
 *   1. ☐ checkbox (selectable only on `Sample collected` rows;
 *      same-encounter constraint enforced on toggle)
 *   2. Order Date  ·  3. Patient Name  ·  4. Contact No  ·  5. MRN
 *   6. Lab Test (with info `(i)` icon when `orderDescription` non-
 *      empty OR `labOrderValueSetTestDetails` non-empty — popover
 *      lists the bundled tests + description)
 *   7. Lab Code (cptCode)  ·  8. Ordered By  ·  9. Encounter Id
 *   10. Lab Vendor (PROD-2653 — `labVendorName`, "—" when unset)
 *   11. Order Status (badge)
 *   12. Actions (icons, status-dependent)
 *
 * Requisitions (PROD-2846): rows that carry a `requisitionId` are grouped
 * into one block per lab, each under a header row (lab name, or "Patient's
 * choice of lab: the patient takes this order to any lab"). Patient's choice
 * rows have nothing to send, so a Requested one offers no collect action.
 * Reflex rows read "Added by the lab". Rows with no requisition (legacy, or
 * before AddLabRequisitions.sql has run) render exactly as before. See
 * `lab-requisition-groups.tsx`.
 *
 * Split clinic panels (PROD-3115): tests of a panel that go to another lab
 * show as a "split part" row in that lab's block, marked "Part of <panel>".
 * Its collect / undo actions change only that part (its own endpoint). It
 * cannot be ticked for the result upload: its result comes back through the
 * lab results flow, which marks its tests resulted.
 *
 * Per-row action icons (Flutter `statusActions()` :850-944):
 *   - `Requested` → ✓ green "Mark as Sample Collected"
 *   - `Sample collected` → upload (blue) "Mark as Result Received"
 *     + revert (blue) "Unmark as Sample Collected"
 *   - `Result received` / `Result reviewed` → eye (blue)
 *     "View Result Received" (opens `documentUrl` in new tab)
 *
 * Bulk action bar (Flutter :535-552): when ≥1 row is selected, an
 * "Upload Lab Result" button appears in the toolbar. Click opens the
 * same upload→register→link dialog with ALL selected rows — backend's
 * `ReceivedLabResults` accepts `labRequestIds: string[]` so a single
 * uploaded doc links to every selected order.
 *
 * Filters (PROD-907 / PROD-908) — `OrderRequestsFilterPanel` slide-
 * out with 5 fields (Patient name, MRN, Order Status, From Date, To
 * Date — Flutter `order_requests_filter.dart` verbatim). Active
 * filters render as removable chips above the table; Status also has
 * an inline quick-pill row mirroring the panel's `status` field for
 * one-click switching. A "Lab vendor" select (PROD-2653) sits beside the
 * Filters button and sends `labVendorId` in the request body. Free-text search box was removed 2026-05-21 —
 * backend `GetPatientLabDetails` ignores the `search` field.
 *
 * Wire shapes verified against the 2026-05-21 5-curl batch — see
 * `docs/CONVERSIONS/lab-linkage/CONVERSION_NOTES.md` §15.
 */

import { formatClockTime } from '@/shared/lib/time-format';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  ClipboardList,
  Eye,
  FileWarning,
  FileText,
  Info,
  RotateCcw,
  SlidersHorizontal,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Segmented, Tag } from '@scripsteam/scrips-signal-ds';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RowActions, type RowAction } from '@/components/blocks/row-actions';
import { BridgeStatusChip } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/auth/auth-store';
import { ListPaginationFooter } from '@/shared/ui/molecules/list-pagination-footer';
import { useLabOrdersList } from '@/scheduling/services/use-lab-orders-list';
import { useUpdateLabOrderStatus } from '@/scheduling/services/use-update-lab-order-status';
import { useUpdateLabSplitPartStatus } from '@/scheduling/services/use-update-lab-split-part-status';
import { useLinkLabResult } from '@/scheduling/services/use-link-lab-result';
import { postCollectionRecorded } from '@/scheduling/services/l360-collection-event';
import { NewFilePopup } from '@/documents/components/new-file-popup';
import { DocumentType } from '@/documents/types/document';
import {
  LAB_ORDER_STATUS,
  type PatientLabOrderDetail,
} from '@/types/scheduling-api';
import {
  EMPTY_ORDER_REQUESTS_FILTERS,
  OrderRequestsFilterPanel,
  type OrderRequestsFilters,
} from './order-requests-filter-panel';
import { EnterResultsDialog } from './enter-results-dialog';
import { LabReportUploadDialog } from './lab-report-upload-dialog';
import { ReportNotReadDialog } from './report-not-read-dialog';
import { isIntakeDeskOn } from '@/lib/intake-switches';
import { useDocumentIntakeEnabled } from '@/settings/services/use-clinic-services';
import { GlyphText } from '@/shared/ui/components/status-glyph';
import { useDeskIntake } from '@/intake/hooks/use-desk-intake';
import { deskIntakeAsks, deskIntakeView } from '@/intake/lib/desk-intake-view';
import { intakeReviewPath } from '@/intake/lib/inbox-view';
import {
  DESK_ENTRY_BELOW,
  readOrderRequestsState,
  withReturnPath,
  writeOrderRequestsState,
  type OrderRequestsDeskState,
} from '@/scheduling/lib/order-requests-return';
import type { LabRequestIntake } from '@/intake/types';
import { SavedLabReportDialog } from './saved-lab-report-dialog';
import { currentReport } from '@/scheduling/lib/saved-lab-report';
import { useLabRequestReports } from '@/scheduling/services/use-lab-request-reports';
import type { LabReportDto } from '@/types/lab-results-api';
import { LabVendorFilter } from './lab-vendor-filter';
import {
  labVendorOptionLabel,
  useLabDeskVendors,
} from '@/scheduling/services/use-lab-desk-vendors';
import { useEncounterLabRequisitions } from '@/scheduling/services/use-encounter-lab-requisitions';
import {
  AddedByLabMarker,
  PATIENT_CHOICE_LABEL,
  PartOfOrderMarker,
  RequisitionHeaderRow,
  type LabDeskEntry,
  type LabDeskRow,
  encountersWithRequisitions,
  groupLabDeskRows,
  isAddedByLab,
  isPatientChoice,
} from './lab-requisition-groups';
import { splitPartErrorMessage } from './lab-split-parts';

/** Checkbox + 11 data columns + Actions. */
const TABLE_COLUMNS = 12;

const PAGE_SIZES = [10, 20, 50] as const;

const STATUS_PILLS = [
  'All',
  LAB_ORDER_STATUS.Requested,
  LAB_ORDER_STATUS.SampleCollected,
  LAB_ORDER_STATUS.ResultReceived,
  LAB_ORDER_STATUS.ResultReviewed,
  LAB_ORDER_STATUS.Cancelled,
] as const;
type StatusPill = (typeof STATUS_PILLS)[number];

/** Format a `YYYY-MM-DD` filter string for the chip strip. Falls back
 *  to the raw value for unparseable input — keeps the chip useful
 *  even when the source is malformed. */
function formatDateChip(value: string): string {
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function OrderRequestsTab() {
  // PROD-3204 slice 4: the lab desk upload and reading (on by default where VITE_DOCUMENT_INTAKE is built; `?x=-intake-desk` opts out). Its "Review values" opens the review page,
  // so only that variant reads the router.
  return isIntakeDeskOn() ? <DeskOrderRequestsTab /> : <OrderRequestsBody openReview={null} />;
}

function DeskOrderRequestsTab() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // Back from the review (Andrew, 23 Oct): the page, filters and scroll this desk was left on, from the address.
  const [initial] = useState(() => readOrderRequestsState(search, PAGE_SIZES));
  const openReview = (path: string, state: OrderRequestsDeskState) => {
    // The desk's state goes into this history entry (replace), so the browser's Back and the review's Back, Close
    // and Confirm all return to it.
    const here = writeOrderRequestsState(pathname, search, state);
    navigate(here, { replace: true });
    navigate(withReturnPath(path, here), { state: DESK_ENTRY_BELOW });
  };
  return <OrderRequestsBody openReview={openReview} initial={initial} />;
}

function OrderRequestsBody({
  openReview,
  initial = {},
}: {
  openReview: ((path: string, state: OrderRequestsDeskState) => void) | null;
  /** The desk's state read from the address (Back from the review); empty otherwise. */
  initial?: Partial<OrderRequestsDeskState>;
}) {
  const organizationId = useAuthStore((s) => s.user?.organizationId);
  const collectorId = useAuthStore((s) => s.user?.userId);

  const [filters, setFilters] = useState<OrderRequestsFilters>(
    initial.filters ?? EMPTY_ORDER_REQUESTS_FILTERS,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(initial.pageNumber ?? 1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>(
    (initial.pageSize as (typeof PAGE_SIZES)[number] | undefined) ?? 10,
  );
  // The list's scroll box: its scroll top goes with the desk's state, and comes back once the rows are in.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollToRestore, setScrollToRestore] = useState(initial.scrollTop ?? 0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Active upload context. When non-null, the NewFilePopup mounts with
  // the patient/encounter + labRequestIds taken from these rows. On
  // save success, we chain `ReceivedLabResults` to link the new
  // document to every order in the target list.
  const [uploadTarget, setUploadTarget] = useState<
    PatientLabOrderDetail[] | null
  >(null);
  // L5: the lab request whose results are being entered.
  const [resultsTarget, setResultsTarget] = useState<PatientLabOrderDetail | null>(null);
  // PROD-3204 slice 4: with `?x=intake-desk` and Clinic services Document Intake on, a report is uploaded to be read.
  const documentIntake = useDocumentIntakeEnabled();
  const deskIntakeOn = openReview !== null && documentIntake.enabled;
  const [deskUploadFrom, setDeskUploadFrom] = useState<PatientLabOrderDetail[] | null>(null);
  const [notRead, setNotRead] = useState<{ row: PatientLabOrderDetail; intake: LabRequestIntake } | null>(null);
  // Reading off: the chosen file goes to today's upload without picking it again.
  const [attachFiles, setAttachFiles] = useState<File[] | null>(null);
  // PROD-3204 slice 5 (`?x=intake-desk`): the lab request whose saved report is open.
  const [reportTarget, setReportTarget] = useState<PatientLabOrderDetail | null>(null);
  const deskOn = isIntakeDeskOn();

  const linkLabResult = useLinkLabResult();
  const vendors = useLabDeskVendors();

  // Backend `GetPatientLabDetails` ignores `search` — confirmed
  // 2026-05-21 by Andrew. Filtering happens via the Filters slide-out
  // (PROD-907) + the inline Status pills (which mirror the panel's
  // `status` field for one-click access). The wire ships exactly the
  // 4 fields Flutter ships on `OrderRequestsListRequest`:
  // organizationId, patientId, status, fromDate, toDate.
  const { data, isLoading, isError } = useLabOrdersList({
    filter: {
      organizationId: organizationId ?? '',
      patientId: filters.patientId ?? null,
      status: filters.status ?? null,
      fromDate: filters.fromDate ?? null,
      toDate: filters.toDate ?? null,
      // PROD-2653: omitted (not null) for "All vendors".
      ...(filters.labVendorId ? { labVendorId: filters.labVendorId } : {}),
      pageNumber,
      pageSize,
    },
  });

  const updateStatus = useUpdateLabOrderStatus();
  const updateSplitPart = useUpdateLabSplitPartStatus();

  const rows = useMemo(
    () => data?.patientLabOrderDetails ?? [],
    [data?.patientLabOrderDetails],
  );

  // PROD-2846: one block per requisition (lab). Only encounters with a
  // requisitioned row are read, so legacy pages make no extra calls.
  const requisitionsByEncounter = useEncounterLabRequisitions(
    encountersWithRequisitions(rows),
  );
  const deskAsks = useMemo(() => (deskIntakeOn ? deskIntakeAsks(rows) : []), [deskIntakeOn, rows]);
  const deskIntake = useDeskIntake(deskAsks, deskIntakeOn);
  const startUpload = (picked: PatientLabOrderDetail[]) =>
    deskIntakeOn ? setDeskUploadFrom(picked) : setUploadTarget(picked);
  const handleIntakeAction = (row: PatientLabOrderDetail, intake: LabRequestIntake) => {
    if (intake.status === 'AwaitingReview') {
      if (row.patientId) {
        openReview?.(intakeReviewPath(row.patientId, intake.runId), {
          pageNumber,
          pageSize,
          filters,
          scrollTop: scrollRef.current?.scrollTop ?? 0,
        });
      }
    } else if (intake.status === 'Failed') {
      setNotRead({ row, intake });
    }
  };

  const entries = groupLabDeskRows(rows, requisitionsByEncounter, {
    labVendorId: filters.labVendorId,
    status: filters.status,
  });

  const handleApplyFilters = (next: OrderRequestsFilters) => {
    setFilters(next);
    setPageNumber(1);
    setSelectedIds(new Set());
  };

  /** Inline status-pills act as a quick toggle for the same `status`
   *  field the filter panel writes. Clicking the active pill clears
   *  it (matches the Flutter dropdown's optional-state behaviour). */
  const handlePillClick = (pill: StatusPill) => {
    const nextStatus =
      pill === 'All'
        ? undefined
        : filters.status === pill
          ? undefined
          : pill;
    handleApplyFilters({ ...filters, status: nextStatus });
  };

  const handleRemoveFilter = (key: keyof OrderRequestsFilters) => {
    if (key === 'patientId') {
      // Clear the patient triple together (id + both display labels)
      // so the chip strip can't leave a dangling label without a
      // patientId backing it.
      handleApplyFilters({
        ...filters,
        patientId: undefined,
        patientName: undefined,
        mrnLabel: undefined,
      });
      return;
    }
    handleApplyFilters({ ...filters, [key]: undefined });
  };

  const handleResetAll = () => handleApplyFilters(EMPTY_ORDER_REQUESTS_FILTERS);

  const pillSelection: StatusPill = filters.status ?? 'All';
  const activeFilterCount =
    (filters.patientId ? 1 : 0) +
    (filters.status ? 1 : 0) +
    (filters.fromDate ? 1 : 0) +
    (filters.toDate ? 1 : 0) +
    (filters.labVendorId ? 1 : 0);

  const selectedVendor = filters.labVendorId
    ? vendors.find((v) => v.id === filters.labVendorId)
    : undefined;

  const handlePageChange = (next: number) => {
    setPageNumber(next);
    setSelectedIds(new Set());
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size as (typeof PAGE_SIZES)[number]);
    setPageNumber(1);
    setSelectedIds(new Set());
  };

  /**
   * Flutter selection rule (:307-369): only `Sample collected` rows
   * can be selected; all selected rows must share the same encounter.
   */
  const toggleRow = (row: PatientLabOrderDetail) => {
    const id = row.id;
    if (!id) return;
    if (row.orderStatus !== LAB_ORDER_STATUS.SampleCollected) return;

    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
      setSelectedIds(next);
      return;
    }

    // Same-encounter constraint (Flutter :336-346)
    if (next.size > 0) {
      const firstSelected = rows.find((r) => r.id && next.has(r.id));
      const expectedEncounter = firstSelected?.encounterId;
      if (
        expectedEncounter &&
        row.encounterId &&
        row.encounterId !== expectedEncounter
      ) {
        toast.error(
          'Only Sample Collected orders with the same Encounter ID can be selected.',
        );
        return;
      }
    }

    next.add(id);
    setSelectedIds(next);
  };

  const selectableRows = useMemo(
    () =>
      rows.filter((r) => r.orderStatus === LAB_ORDER_STATUS.SampleCollected),
    [rows],
  );

  const allSelectedOnPage =
    selectableRows.length > 0 &&
    selectableRows.every((r) => r.id && selectedIds.has(r.id));

  const handleSelectAll = () => {
    if (allSelectedOnPage) {
      setSelectedIds(new Set());
      return;
    }
    if (selectableRows.length === 0) return;
    // Select-all picks the first encounter group only (same-encounter
    // constraint applies to bulk select too).
    const firstEncounter = selectableRows[0].encounterId;
    const next = new Set<string>();
    for (const r of selectableRows) {
      if (r.id && r.encounterId === firstEncounter) next.add(r.id);
    }
    setSelectedIds(next);
  };

  const handleStatusChange = (row: LabDeskRow, nextStatus: string) => {
    // L360 L2: after a collection save succeeds, tell the care model. The
    // post is fire-and-forget and never touches the save outcome.
    const notifyCollected = (labRequestId: string, requisitionId?: string) => {
      if (nextStatus !== LAB_ORDER_STATUS.SampleCollected) return;
      if (!row.patientId || !collectorId) return;
      postCollectionRecorded({
        patientId: row.patientId,
        labRequestId,
        requisitionId,
        collectorId,
      });
    };
    // mutateAsync (not per-call mutate callbacks): those fire only for the
    // latest call and not after unmount, so quick back-to-back rows would lose posts.
    // PROD-3115: a split part changes only its own tests.
    if (row.splitPart) {
      const { labRequestId, requisitionId } = row.splitPart;
      updateSplitPart
        .mutateAsync({ labRequestId, requisitionId, status: nextStatus })
        .then(
          () => notifyCollected(labRequestId, requisitionId),
          (err: unknown) => toast.error(splitPartErrorMessage(err)),
        )
        .catch(() => {});
      return;
    }
    if (!row.id) return;
    const orderId = row.id;
    // The whole-order hook has no error handler of its own: a failure stays silent, as before.
    updateStatus
      .mutateAsync({ id: orderId, status: nextStatus })
      .then(() => notifyCollected(orderId))
      .catch(() => {});
  };

  useEffect(() => {
    if (!scrollToRestore || rows.length === 0 || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollToRestore;
    setScrollToRestore(0);
  }, [scrollToRestore, rows.length]);

  const handleOpenBulkUpload = () => {
    const picked = rows.filter((r) => r.id && selectedIds.has(r.id));
    if (picked.length === 0) return;
    startUpload(picked);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header — section title + bulk-upload button when rows are
          selected + Filters button + Status quick-pills. */}
      <div className="px-[var(--page-pad)] pt-[var(--page-header-gap)] pb-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Status quick-pills — same `status` field the panel writes;
              Flutter has only the panel, but the pills are a useful
              React-side affordance for one-click status switching. */}
          {/* Single-select exclusive status switch. `pillSelection` is
              never "none" (defaults to 'All'), so `value` is always set.
              Contract (verified against scrips-signal-ds@1.5.0 source):
              Segmented's `select` calls `onChange?.(v)` UNCONDITIONALLY —
              including a re-click of the already-selected option — so the
              Flutter "click active to clear" toggle in handlePillClick
              (`filters.status === pill ? undefined : pill`) is preserved.
              Pinned by the "re-clicking the active status clears the filter"
              test — if a DS patch ever drops the re-click onChange, it fails. */}
          <div>
            <Segmented
              options={STATUS_PILLS.map((s) => ({ value: s, label: s }))}
              value={pillSelection}
              onChange={handlePillClick}
              aria-label="Order status"
            />
          </div>
          <div className="flex items-center gap-3">
            <LabVendorFilter
              value={filters.labVendorId}
              vendors={vendors}
              onChange={(labVendorId) =>
                handleApplyFilters({ ...filters, labVendorId })
              }
            />
            {selectedIds.size > 0 ? (
              <Button
                size="sm"
                // PROD-3204 (Antonin): upload is secondary on the lab desk; Create stays the one primary.
                variant={deskIntakeOn ? 'outline' : 'default'}
                onClick={handleOpenBulkUpload}
                disabled={updateStatus.isPending}
              >
                {deskIntakeOn ? `Upload lab report (${selectedIds.size})` : `Upload Lab Result (${selectedIds.size})`}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFilterPanelOpen(true)}
              aria-label="Open filters"
            >
              <SlidersHorizontal
                className="w-4 h-4 mr-2"
                aria-hidden
              />
              Filters
              {activeFilterCount > 0 ? (
                <span className="ml-2 inline-flex items-center justify-center rounded-full bg-brand-primary text-white text-xs font-bold w-4 h-4">
                  {activeFilterCount}
                </span>
              ) : null}
            </Button>
          </div>
        </div>

        {/* Active-filter chip strip (PROD-908) — Flutter
            `order_requests_tab.dart:485-535`. Hidden when no filters
            are applied. Status is intentionally included here so the
            chip and the pill stay in sync — clicking either clears
            the same field. */}
        {activeFilterCount > 0 ? (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {filters.patientName ? (
              <FilterChip
                label={`Patient: ${filters.patientName}`}
                onRemove={() => handleRemoveFilter('patientId')}
              />
            ) : null}
            {filters.mrnLabel ? (
              <FilterChip
                label={`MRN: ${filters.mrnLabel}`}
                onRemove={() => handleRemoveFilter('patientId')}
              />
            ) : null}
            {/* Patient picked but no display label cached — defensive
                chip so a refresh that lost the label doesn't strand
                the patientId invisibly on the wire. */}
            {filters.patientId &&
            !filters.patientName &&
            !filters.mrnLabel ? (
              <FilterChip
                label="Patient: (selected)"
                onRemove={() => handleRemoveFilter('patientId')}
              />
            ) : null}
            {filters.status ? (
              <FilterChip
                label={`Status: ${filters.status}`}
                onRemove={() => handleRemoveFilter('status')}
              />
            ) : null}
            {filters.labVendorId ? (
              <FilterChip
                label={`Vendor: ${
                  selectedVendor ? labVendorOptionLabel(selectedVendor) : '(selected)'
                }`}
                onRemove={() => handleRemoveFilter('labVendorId')}
              />
            ) : null}
            {filters.fromDate ? (
              <FilterChip
                label={`From: ${formatDateChip(filters.fromDate)}`}
                onRemove={() => handleRemoveFilter('fromDate')}
              />
            ) : null}
            {filters.toDate ? (
              <FilterChip
                label={`To: ${formatDateChip(filters.toDate)}`}
                onRemove={() => handleRemoveFilter('toDate')}
              />
            ) : null}
            <button
              type="button"
              onClick={handleResetAll}
              className="text-sm font-medium text-brand-primary hover:text-brand-depth ml-1"
            >
              Reset all
            </button>
          </div>
        ) : null}
      </div>

      {/* Body — the only scrolling region; the list is one card inset by the page padding
          (the list-table standard, src/index.css). `overflow: clip` on the card instead of the
          standard's `hidden`: a clipped card is not a scroll container, so the sticky header
          keeps sticking to this scroll region. `w-fit min-w-full`: a table wider than the
          tab grows the card with it, so the region scrolls sideways instead of clipping columns. */}
      <div ref={scrollRef} data-order-requests-scroll className="flex-1 min-h-0 overflow-auto scrollbar-hide px-[var(--page-pad)] pb-[var(--page-pad)]">
        <section className="list-table w-fit min-w-full">
          <div className="list-table-title">
            <div className="flex items-baseline gap-2">
              <h2>Order Requests</h2>
              {data?.totalNumberOfRecords != null ? (
                <p className="text-[length:var(--type-label-size)] font-normal text-text-secondary">
                  {data.totalNumberOfRecords} total
                </p>
              ) : null}
            </div>
          </div>
        {isLoading ? (
          <LoadingTable />
        ) : isError ? (
          <ErrorState />
        ) : rows.length === 0 ? (
          <EmptyState />
        ) : (
          <OrdersTable
            entries={entries}
            selectedIds={selectedIds}
            allSelectedOnPage={allSelectedOnPage}
            anySelectable={selectableRows.length > 0}
            busy={updateStatus.isPending || updateSplitPart.isPending}
            onToggleRow={toggleRow}
            onSelectAll={handleSelectAll}
            onStatusChange={handleStatusChange}
            onLinkResult={(row) => startUpload([row])}
            onEnterResults={(row) => setResultsTarget(row)}
            deskIntake={deskIntakeOn ? deskIntake : undefined}
            onIntakeAction={handleIntakeAction}
            onViewReport={deskOn ? (row) => setReportTarget(row) : undefined}
            // PROD-3204 (PmDesk, `?x=intake-desk`): the lab is the group header, not a column, so the desk fits at 1440 wide.
            vendorInGroupHeader={deskOn}
          />
        )}
        </section>
      </div>

      {/* Footer */}
      {!isLoading && !isError && data ? (
        <ListPaginationFooter
          pageNumber={data.pageNumber ?? pageNumber}
          pageSize={pageSize}
          pageSizes={PAGE_SIZES}
          totalRecords={data.totalNumberOfRecords ?? 0}
          totalPages={data.totalNumberOfPages ?? 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      ) : null}

      <OrderRequestsFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        value={filters}
        onApply={handleApplyFilters}
        organizationId={organizationId ?? ''}
      />

      {/* Upload Lab Result popup — reuses the shared Documents
          `NewFilePopup` with the type picker locked to Lab (Andrew
          direction 2026-05-21: same UX as the regular Documents
          upload, just constrained to the Labs tab). `onSaved` chains
          `ReceivedLabResults` so the uploaded document is linked to
          every selected order in one transaction. */}
      {resultsTarget?.id && resultsTarget.patientId ? (
        <EnterResultsDialog
          open
          onOpenChange={(open) => {
            if (!open) setResultsTarget(null);
          }}
          patientId={resultsTarget.patientId}
          labRequestId={resultsTarget.id}
          patientName={resultsTarget.patientName ?? ''}
          requestStatus={resultsTarget.orderStatus}
        />
      ) : null}

      <LabReportUploadDialog
        rows={rows}
        from={deskUploadFrom}
        onClose={() => setDeskUploadFrom(null)}
        onUploaded={(outcome) => {
          setDeskUploadFrom(null);
          setSelectedIds(new Set());
          if (outcome === 'reading') toast.success('The report is being read. You can leave this page.');
          else toast.warning('The report is stored, but reading could not start. Open the row to read it again.');
        }}
        onAttachOnly={(file, requests) => {
          setDeskUploadFrom(null);
          setAttachFiles([file]);
          setUploadTarget(requests);
        }}
      />

      <ReportNotReadDialog
        target={notRead}
        onClose={() => setNotRead(null)}
        onUploadAgain={(row) => {
          setNotRead(null);
          setDeskUploadFrom([row]);
        }}
        onEnterResults={(row) => {
          setNotRead(null);
          setResultsTarget(row);
        }}
      />
      <SavedLabReportDialog row={reportTarget} onClose={() => setReportTarget(null)} />

      {uploadTarget && uploadTarget[0]?.patientId ? (
        <NewFilePopup
          open
          onOpenChange={(open) => {
            if (!open) {
              setUploadTarget(null);
              setAttachFiles(null);
              setSelectedIds(new Set());
            }
          }}
          initialFiles={attachFiles ?? undefined}
          patientId={uploadTarget[0].patientId}
          encounterId={uploadTarget[0].encounterId ?? null}
          lockedType={DocumentType.Labs}
          onSaved={async (documentId) => {
            // documentId is the bare-string GUID from
            // AddUpdatePatientDocument (curl-4 wire shape). Link it to
            // every selected lab request in one ReceivedLabResults
            // call; the hook invalidates `['lab-orders']` on success
            // so the table refetches and the rows flip to
            // `Result received`.
            const ids = uploadTarget
              .map((r) => r.id)
              .filter((id): id is string => !!id);
            if (ids.length === 0) return;
            await linkLabResult.mutateAsync({
              request: { labRequestIds: ids, patientDocumentId: documentId },
            });
          }}
        />
      ) : null}
    </div>
  );
}

// ── Filter chip strip (PROD-908) ────────────────────────────────────────

/**
 * Single removable chip in the active-filter strip — Signal `Tag` with its
 * built-in `onRemove` × (PROD-1034). Replaces the previous hand-rolled
 * child-`<button>` × hack. Same shape as the canonical reports
 * `filter-chips-bar.tsx` chip, which migrated to the same primitive.
 */
function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <Tag
      className="whitespace-nowrap"
      onRemove={onRemove}
      removeLabel={`Remove filter ${label}`}
    >
      {label}
    </Tag>
  );
}

// ── Table ────────────────────────────────────────────────────────────────

function OrdersTable({
  entries,
  selectedIds,
  allSelectedOnPage,
  anySelectable,
  busy,
  onToggleRow,
  onSelectAll,
  onStatusChange,
  onLinkResult,
  onEnterResults,
  deskIntake,
  onIntakeAction,
  onViewReport,
  vendorInGroupHeader = false,
}: {
  entries: LabDeskEntry[];
  selectedIds: Set<string>;
  allSelectedOnPage: boolean;
  anySelectable: boolean;
  busy: boolean;
  onToggleRow: (row: PatientLabOrderDetail) => void;
  onSelectAll: () => void;
  onStatusChange: (row: LabDeskRow, nextStatus: string) => void;
  onLinkResult: (row: PatientLabOrderDetail) => void;
  onEnterResults: (row: PatientLabOrderDetail) => void;
  /**
   * PROD-3204 (`?x=intake-desk`, board PmDesk): no Lab Vendor column. The lab is the requisition group's header; a row with no
   * group (an older order) names its lab under the test.
   */
  vendorInGroupHeader?: boolean;
  /** PROD-3204 slice 4: each request's latest lab-desk run; absent when the desk does not read reports. */
  deskIntake?: Map<string, LabRequestIntake | null>;
  onIntakeAction: (row: PatientLabOrderDetail, intake: LabRequestIntake) => void;
  /** PROD-3204 slice 5: set only behind `?x=intake-desk`. */
  onViewReport?: (row: PatientLabOrderDetail) => void;
}) {
  // Every full-width row spans exactly the columns the header shows (no Lab Vendor column behind ?x=intake-desk).
  const columnCount = vendorInGroupHeader ? TABLE_COLUMNS - 1 : TABLE_COLUMNS;
  return (
    <table className="w-full">
      <thead className="sticky top-0 z-10">
        <tr>
          <th className="w-10 px-2 py-2">
            <Checkbox
              checked={allSelectedOnPage}
              onCheckedChange={onSelectAll}
              disabled={!anySelectable}
              aria-label="Select all Sample Collected on page (same encounter)"
            />
          </th>
          <Th>Order Date</Th>
          <Th>Patient Name</Th>
          <Th>Contact No</Th>
          <Th>MRN</Th>
          <Th>Lab Test</Th>
          <Th>Lab Code</Th>
          <Th>Ordered By</Th>
          <Th>Encounter ID</Th>
          {vendorInGroupHeader ? null : <Th>Lab Vendor</Th>}
          <Th>{deskIntake ? 'Order Status · Report' : 'Order Status'}</Th>
          <th
            aria-label="Actions"
            className="text-right"
          >
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => {
          const renderRow = (row: LabDeskRow) => (
            <OrderRow
              key={
                row.splitPart
                  ? `${row.id}:${row.splitPart.requisitionId}`
                  : (row.id ?? Math.random())
              }
              row={row}
              selected={!row.splitPart && !!row.id && selectedIds.has(row.id)}
              busy={busy}
              onToggleRow={onToggleRow}
              onStatusChange={onStatusChange}
              onLinkResult={onLinkResult}
              onEnterResults={onEnterResults}
              intake={!row.splitPart && row.id ? deskIntake?.get(row.id) : undefined}
              onIntakeAction={onIntakeAction}
              onViewReport={onViewReport}
              vendorInGroupHeader={vendorInGroupHeader}
              grouped={entry.kind !== 'row'}
            />
          );
          if (entry.kind === 'row') return renderRow(entry.row);
          return (
            <Fragment key={`req-${entry.requisitionId}`}>
              <RequisitionHeaderRow entry={entry} colSpan={columnCount} />
              {entry.rows.map(renderRow)}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function OrderRow({
  row,
  selected,
  busy,
  onToggleRow,
  onStatusChange,
  onLinkResult,
  onEnterResults,
  intake,
  onIntakeAction,
  onViewReport,
  vendorInGroupHeader = false,
  grouped = false,
}: {
  row: LabDeskRow;
  selected: boolean;
  busy: boolean;
  onToggleRow: (row: PatientLabOrderDetail) => void;
  onStatusChange: (row: LabDeskRow, nextStatus: string) => void;
  onLinkResult: (row: PatientLabOrderDetail) => void;
  onEnterResults: (row: PatientLabOrderDetail) => void;
  intake?: LabRequestIntake | null;
  onIntakeAction: (row: PatientLabOrderDetail, intake: LabRequestIntake) => void;
  /** PROD-3204: the desk shows the lab in the group header (no Lab Vendor column). */
  vendorInGroupHeader?: boolean;
  /** The row sits under a requisition group header that names its lab. */
  grouped?: boolean;
  onViewReport?: (row: PatientLabOrderDetail) => void;
}) {
  const id = row.id ?? '';
  const desk = deskIntakeView(intake);
  const status = row.orderStatus ?? LAB_ORDER_STATUS.Requested;
  // PROD-3115: a split part's result comes through the lab results flow,
  // so it is never ticked for the result upload.
  const splitPart = row.splitPart;
  const canSelect = !splitPart && status === LAB_ORDER_STATUS.SampleCollected;
  // Flutter `:717-718`: info icon shows when either field is non-empty.
  const hasTestInfo =
    !!row.orderDescription?.trim() ||
    (row.labOrderValueSetTestDetails?.length ?? 0) > 0;
  const patientChoice = isPatientChoice(row);
  const hasReport = useHasSavedReport(
    !splitPart && !!onViewReport && hasResultStatus(status) ? row.patientId : null,
    id,
  );

  return (
    <tr
      className={cn(
        'hover:bg-grey-100/60',
        selected && 'bg-blue-100/30',
      )}
    >
      <td className="px-2 py-2 align-middle">
        {canSelect ? (
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleRow(row)}
            aria-label={`Select order ${id}`}
          />
        ) : null}
      </td>
      <Td>{fmtDateTime(row.orderDate)}</Td>
      <Td className="font-semibold text-text-primary">
        {row.patientName ?? '—'}
      </Td>
      <Td className="text-text-muted">{row.contactNumber ?? '—'}</Td>
      <Td className="text-text-muted">{row.mrn ?? '—'}</Td>
      <Td className="max-w-[240px]">
        <div className="flex items-center gap-2">
          <span className="truncate" title={row.orderName ?? undefined}>
            {row.orderName ?? '—'}
          </span>
          {hasTestInfo ? <LabTestInfoButton row={row} /> : null}
          {splitPart ? <PartOfOrderMarker orderName={splitPart.orderName} /> : null}
          {isAddedByLab(row) ? <AddedByLabMarker /> : null}
        </div>
        {vendorInGroupHeader && !grouped ? (
          <div className="mt-1 truncate text-xs text-text-muted" title={row.labVendorName ?? undefined}>
            {patientChoice ? PATIENT_CHOICE_LABEL : row.labVendorName?.trim() || 'No lab'}
          </div>
        ) : null}
        {!splitPart && row.patientId && id && hasResultStatus(status) ? (
          <SavedValuesLine patientId={row.patientId} labRequestId={id} />
        ) : null}
      </Td>
      <Td className="text-text-muted">{row.cptCode ?? '—'}</Td>
      <Td>{row.orderReqBy ?? '—'}</Td>
      <Td className="text-text-muted font-mono text-sm">
        {row.encounterNumber ?? '—'}
      </Td>
      {vendorInGroupHeader ? null : (
        <Td className="text-text-muted" title={row.labVendorName ?? undefined}>
          {patientChoice
            ? PATIENT_CHOICE_LABEL
            : row.labVendorName?.trim() || '—'}
        </Td>
      )}
      <Td>
        {renderStatus(status)}
        {desk.line ? (
          <div className="mt-1" data-testid={`order-report-${id}`}>
            {'chip' in desk.line ? (
              <BridgeStatusChip semantic={desk.line.chip.semantic}>{desk.line.chip.label}</BridgeStatusChip>
            ) : (
              <GlyphText kind={desk.line.glyph.kind}>{desk.line.glyph.label}</GlyphText>
            )}
          </div>
        ) : null}
      </Td>
      {/* Fixed-width actions cell (matches `<th>` width above) so the
          ResultReceived "—" placeholder doesn't shift the column when
          documentUrl is absent — PR #337 review: layout-shift catch. */}
      <td className="px-2 py-2">
        <StatusActions
          id={id}
          status={status}
          documentUrl={row.documentUrl}
          patientChoice={patientChoice}
          busy={busy}
          onStatusChange={(next) => onStatusChange(row, next)}
          // PROD-3204 slice 4: no second upload while this request's report is being read or waits for review.
          onLinkResult={splitPart || desk.busy ? undefined : () => onLinkResult(row)}
          onEnterResults={splitPart ? undefined : () => onEnterResults(row)}
          intakeAction={
            intake && desk.action
              ? {
                  label: desk.action === 'review' ? 'Review values' : 'Why it was not read',
                  icon: desk.action === 'review' ? <Eye className="size-4" aria-hidden /> : <FileWarning className="size-4" aria-hidden />,
                  onClick: () => onIntakeAction(row, intake),
                }
              : undefined
          }
          onViewReport={hasReport && onViewReport ? () => onViewReport(row) : undefined}
        />
      </td>
    </tr>
  );
}

/** A row's values line is cached. The dialog save invalidates the key, so a save still refreshes it. */
export const SAVED_VALUES_STALE_MS = 5 * 60_000;

function hasResultStatus(status: string): boolean {
  return status === LAB_ORDER_STATUS.ResultReceived || status === LAB_ORDER_STATUS.ResultReviewed;
}

/**
 * PROD-3204 slice 5: true when the request has a saved report. It shares the values line's query (same key and
 * stale time), so it adds no request. A null patient asks nothing.
 */
function useHasSavedReport(patientId: string | null | undefined, labRequestId: string): boolean {
  const { data } = useLabRequestReports(patientId ?? '', labRequestId, !!patientId, {
    staleTime: SAVED_VALUES_STALE_MS,
  });
  return !!patientId && currentReport(data?.reports) !== null;
}

/** The saved values of the current report, e.g. "HbA1c 5.9 % · Glucose 99 mg/dL". Empty for PDF-only results. */
function SavedValuesLine({ patientId, labRequestId }: { patientId: string; labRequestId: string }) {
  const { data } = useLabRequestReports(patientId, labRequestId, true, {
    staleTime: SAVED_VALUES_STALE_MS,
  });
  const current = data?.reports?.reduce<LabReportDto | undefined>(
    (best, r) => (!best || r.version > best.version ? r : best),
    undefined,
  );
  // Numeric results only. A text result is skipped, and the unit shows only when present.
  const parts = (current?.observations ?? [])
    .filter((o) => typeof o.valueNumeric === 'number' && Number.isFinite(o.valueNumeric))
    .map((o) => [o.display ?? o.loinc, o.valueNumeric, o.unit?.trim()].filter((x) => x !== '' && x != null).join(' '));
  if (parts.length === 0) return null;
  const text = parts.join(' · ');
  return (
    <div className="mt-1 truncate text-xs text-text-muted" title={text}>
      {text}
    </div>
  );
}

// ── Per-row action icons (Flutter `statusActions()` :850-944) ──────────

function StatusActions({
  id,
  status,
  documentUrl,
  patientChoice = false,
  busy,
  onStatusChange,
  onLinkResult,
  onEnterResults,
  intakeAction,
  onViewReport,
}: {
  id: string;
  status: string;
  documentUrl: string | null | undefined;
  /** PROD-2846: Patient's choice of lab: nothing to collect or send. */
  patientChoice?: boolean;
  busy: boolean;
  onStatusChange: (nextStatus: string) => void;
  /** Absent for a split part (PROD-3115): its result is not uploaded here. */
  onLinkResult?: () => void;
  /** L5: absent for a split part. */
  onEnterResults?: () => void;
  /** PROD-3204 slice 4: the lab-desk run's next step (Review values, or why the report was not read). Listed first. */
  intakeAction?: { label: string; icon: React.ReactNode; onClick: () => void };
  /** PROD-3204 slice 5: present when the request has a saved report and `?x=intake-desk` is on. */
  onViewReport?: () => void;
}) {
  if (!id) return null;
  const enterResults: RowAction | null = onEnterResults
    ? { label: 'Enter results', icon: <ClipboardList className="size-4" aria-hidden />, onClick: onEnterResults, disabled: busy }
    : null;

  const noActions = (
    <span
      aria-hidden
      data-testid="order-request-no-actions"
      className="inline-flex items-center justify-end text-xs text-text-muted"
    >
      —
    </span>
  );

  // PROD-2660: a Cancelled request is terminal — no collect, no result link,
  // no undo. PROD-2846: a Patient's choice order still Requested has nothing
  // to send (the patient takes it to any lab). Both show the placeholder.
  if (
    status === LAB_ORDER_STATUS.Cancelled ||
    (patientChoice && status === LAB_ORDER_STATUS.Requested)
  ) {
    return noActions;
  }

  const actions: RowAction[] = intakeAction ? [{ ...intakeAction, disabled: busy }] : [];
  if (status === LAB_ORDER_STATUS.Requested) {
    actions.push({
      label: 'Mark as sample collected',
      icon: <Check className="size-4" aria-hidden />,
      onClick: () => onStatusChange(LAB_ORDER_STATUS.SampleCollected),
      disabled: busy,
    });
  } else if (status === LAB_ORDER_STATUS.SampleCollected) {
    if (onLinkResult) {
      actions.push({ label: 'Mark as result received', icon: <Upload className="size-4" aria-hidden />, onClick: onLinkResult, disabled: busy });
    }
    if (enterResults) actions.push(enterResults);
    actions.push({
      label: 'Unmark as sample collected',
      icon: <RotateCcw className="size-4" aria-hidden />,
      onClick: () => onStatusChange(LAB_ORDER_STATUS.Requested),
      disabled: busy,
    });
  } else if (
    // ResultReceived / ResultReviewed — PM branch only (PROD-912: no "Mark as
    // Reviewed" dropdown). The View action only shows when a document exists.
    status === LAB_ORDER_STATUS.ResultReceived ||
    status === LAB_ORDER_STATUS.ResultReviewed
  ) {
    if (documentUrl) {
      actions.push({
        label: 'View result received',
        icon: <Eye className="size-4" aria-hidden />,
        onClick: () => window.open(documentUrl, '_blank', 'noopener,noreferrer'),
        disabled: busy,
      });
    }
    if (onViewReport) {
      actions.push({ label: 'View lab report', icon: <FileText className="size-4" aria-hidden />, onClick: onViewReport, disabled: busy });
    }
    if (enterResults && status === LAB_ORDER_STATUS.ResultReceived) actions.push(enterResults);
  }

  if (actions.length === 0) return noActions;
  return <RowActions actions={actions} label={`order ${id}`} />;
}

// ── Info dialog next to Lab Test cell (Flutter `showLabTestInfo` :1209-1335)

/**
 * Lab Test Info dialog. Mirrors Flutter `showLabTestInfo` — opens a
 * modal listing the bundled sub-tests, the order description, and any
 * note-to-lab attached to the order. Keyed off four optional fields:
 *   - `orderName` → header
 *   - `orderDescription` → description paragraph
 *   - `labOrderValueSetTestDetails` → sub-test list
 *   - `notePlainText` → note-to-lab (free-text clinical context the
 *     requesting provider attached for the lab; ships as `null` for
 *     most rows but Flutter renders it when present).
 *
 * Trigger lives next to the Lab Test cell and only appears when at
 * least one of `orderDescription` / `labOrderValueSetTestDetails` is
 * populated (parent guard in `OrderRow`). The note-to-lab section is
 * gated on a trimmed-non-empty check — empty strings and whitespace
 * never produce a section heading.
 */
function LabTestInfoButton({ row }: { row: PatientLabOrderDetail }) {
  const [open, setOpen] = useState(false);
  const tests = row.labOrderValueSetTestDetails ?? [];
  // Trim before truthiness check — guards against `""`, `null`,
  // `undefined`, and whitespace-only strings (PR #337 review §2).
  const description = row.orderDescription?.trim() ?? '';
  const noteToLab = row.notePlainText?.trim() ?? '';
  // shadcn `DialogContent` warns when there is no `DialogDescription`
  // (aria-describedby gap; some screen readers silently skip the body
  // when absent). Provide a fallback string when the order has no
  // `orderDescription` so the dialog is always accessible
  // (PR #337 review §1).
  const a11yDescription =
    description ||
    (tests.length > 0 || noteToLab
      ? `Bundled tests and notes for ${row.orderName ?? 'this lab order'}.`
      : `Details for ${row.orderName ?? 'this lab order'}.`);

  return (
    <>
      <button
        type="button"
        aria-label="Show bundled tests"
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-5 h-5 rounded text-brand-primary hover:bg-blue-100 shrink-0"
      >
        <Info className="h-4 w-4" aria-hidden />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--color-brand-depth)]">
              {row.orderName ?? 'Lab Test'}
            </DialogTitle>
            {/* `DialogDescription` is ALWAYS rendered — the visible
                description text matches the `orderDescription` when
                present, otherwise falls back to a generic phrase that
                still labels the dialog body for assistive tech. */}
            <DialogDescription
              className={!description ? 'sr-only' : undefined}
            >
              {a11yDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {tests.length > 0 ? (
              <div>
                <p className="text-xs font-medium text-text-muted mb-1">
                  Tests ({tests.length})
                </p>
                <ul className="space-y-2 max-h-72 overflow-y-auto scrollbar-hide">
                  {tests.map((t, i) => (
                    <li
                      key={`${t.code}-${i}`}
                      className="text-sm text-text-primary"
                    >
                      <span className="font-mono text-text-muted">
                        {t.code}
                      </span>
                      {' · '}
                      <span>{t.display}</span>
                      {t.system ? (
                        <span className="ml-1 text-xs text-text-muted">
                          ({t.system})
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {noteToLab ? (
              <div>
                <p className="text-xs font-medium text-text-muted mb-1">
                  Note to lab
                </p>
                <p className="text-sm text-text-primary whitespace-pre-wrap">
                  {noteToLab}
                </p>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── States ───────────────────────────────────────────────────────────────

function LoadingTable() {
  return (
    <div className="p-6 space-y-3">
      <Skeleton className="h-5 w-1/3" />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function ErrorState() {
  return (
    <div className="p-12 text-center">
      <AlertCircle className="h-8 w-8 text-status-error mx-auto" />
      <p className="mt-2 text-sm font-semibold text-status-error">
        Couldn't load lab orders
      </p>
      <p className="mt-1 text-sm text-text-muted">
        Try refreshing the page. If the issue persists, contact support.
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-12 text-center">
      <p className="text-sm font-semibold text-text-primary">
        No lab orders found
      </p>
      <p className="mt-1 text-sm text-text-muted">
        Try changing the search query or the status filter.
      </p>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left">
      {children}
    </th>
  );
}

function Td({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td
      title={title}
      className={cn(
        'text-text-primary align-middle',
        className,
      )}
    >
      {children}
    </td>
  );
}

/**
 * "5:42a, Apr 22, 2026" — matches Flutter Order Date format.
 */
function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return `${formatClockTime(d)}, ${date}`;
  } catch {
    return iso;
  }
}

function renderStatus(status: string) {
  const raw = (status ?? '').trim();
  if (!raw) return <span className="text-xs text-text-muted">—</span>;
  switch (raw) {
    case LAB_ORDER_STATUS.Requested:
      return <BridgeStatusChip semantic="warning">{raw}</BridgeStatusChip>;
    case LAB_ORDER_STATUS.SampleCollected:
      return <BridgeStatusChip semantic="info">{raw}</BridgeStatusChip>;
    case LAB_ORDER_STATUS.ResultReceived:
    case LAB_ORDER_STATUS.ResultReviewed:
      return <BridgeStatusChip semantic="success">{raw}</BridgeStatusChip>;
    case LAB_ORDER_STATUS.Cancelled:
      // PROD-2660: Cancelled reads as void — muted neutral, label struck through.
      return <BridgeStatusChip semantic="neutral" className="line-through">{raw}</BridgeStatusChip>;
    default:
      return (
        <BridgeStatusChip semantic="neutral" title={`Unmapped status: ${raw}`}>
          {raw}
        </BridgeStatusChip>
      );
  }
}
